import { useAppDispatch, useAppSelector } from "index";
import { ApplicationState } from "store/index";
import {
  keyframes,
  Paper,
  Typography,
  List,
  ListItemIcon,
  ListItemText,
  ClickAwayListener,
  Container,
} from "@mui/material";
import Button from "@mui/material/Button";
import Dropzone from "containers/Components/Dropzone";
import useLanguageHook from "containers/hooks/useLanguageHook";
import { Stack, styled } from "@mui/system";
import routes from "@config/routes";
import Link from "@mui/material/Link";
import ListItem from "@mui/material/ListItem";
import { useNavigate } from "react-router-dom";
import { useWindowSize } from "../hooks/useWindowSize";
import { clearLastFilename } from "@store/fileParsing/actions";

// Component-specific props.
type ComponentProps = {
  exportId: string;
};

const ring = keyframes`
  from {
    width: 50%;
    height: 100%;
    opacity: 0;
  }
  50% {
    width: 80%;
    height: 110%;
    opacity: 0.2;
  }
  to {
    width: 260%;
    height: 260%;
    opacity: 0;
  }
`;

const AnimatedPulsatingButton = styled(Button)(({ theme }) => ({
  "&::before": {
    content: '""',
    width: "100%",
    height: "100%",
    position: "absolute",
    overflow: "hidden",
    zIndex: 0,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    animation: `${ring} 1500ms linear 0ms infinite`,
    borderRadius: "100%",
    border: "6px solid " + theme.palette.primary.light,
  },
}));

function ProviderItemExpanded(props: ComponentProps) {
  const providers = useAppSelector((state: ApplicationState) => state.data.providers);
  const files = useAppSelector((state: ApplicationState) => state.data.exports);
  const item = providers.filter((d) => d.id === props.exportId)[0];
  const { lang, t } = useLanguageHook();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  if (item === undefined || props.exportId === undefined)
    return <Typography>Error: unknown providerId: {props.exportId}</Typography>;

  return (
    <ClickAwayListener
      onClickAway={() => {
        dispatch(clearLastFilename());
        navigate("..");
      }}
    >
      <Paper
        sx={{
          margin: "auto",
          width: "100%",
          maxWidth: 786,
          padding: 3,
          overflow: "hidden",
        }}
      >
        <Typography variant="h3">
          {t.DataPage.First} {item.name}
        </Typography>
        <InstructionList list={item.strings[lang].help} screenshots={item.screenshots} />
        <Dropzone />
        {files.length > 0 && (
          <Container
            sx={{
              mt: 3,
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
            }}
          >
            <AnimatedPulsatingButton
              variant="contained"
              color="primary"
              onClick={() => navigate(routes.Application + routes.App.Timeline)}
            >
              {t.DataPage.ExploreActionButton}
            </AnimatedPulsatingButton>
          </Container>
        )}
      </Paper>
    </ClickAwayListener>
  );
}

function InstructionList({ list, screenshots }: { list: string[]; screenshots?: (string | undefined)[] }) {
  const { t } = useLanguageHook();
  const windowHeight = useWindowSize().height;

  return (
    <List>
      {list.map((d, i) => (
        <ListItem key={i} sx={{ alignItems: "flex-start" }}>
          <ListItemIcon sx={{ minWidth: "14px", pr: 2 }}>
            <Typography variant="h3">{i + 1}</Typography>
          </ListItemIcon>
          <Stack direction="column">
            {d.startsWith("http") ? (
              <ListItemText
                key={i}
                sx={{ mt: 0 }}
                primary={
                  <>
                    {t.DataPage.VisitWebsiteFirst}{" "}
                    <Link target="_blank" rel="noopener noreferrer" href={d}>
                      {t.DataPage.VisitWebsiteSecond} ({d})
                    </Link>{" "}
                    {t.DataPage.VisitWebsiteThird}
                  </>
                }
              />
            ) : (
              <ListItemText key={i} sx={{ mt: 0 }} primary={d} />
            )}
            <img src={screenshots?.[i - 1] ?? ""} style={{ maxWidth: "100%", maxHeight: windowHeight * 0.3 }} />
          </Stack>
        </ListItem>
      ))}
    </List>
  );
}

export default ProviderItemExpanded;
